import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Stack,
  Text,
  ScaleFade,
} from '@chakra-ui/react';
import { Link as ReactLink, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BrandHeading2 } from 'src/components/BrandHeading.jsx';
import Cookies from 'universal-cookie';
import { Helmet } from 'react-helmet';
import Testimonial from 'src/components/Testimonial.jsx';
import TrackWidget from 'src/components/Tracker/TrackWidget.jsx';
import StaticNotifications from 'src/components/Landing/StaticNotifications.jsx';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import { DISCOUNT_CODE } from '#constants';
import MetaInfo from 'src/components/MetaInfo';

const cookies = new Cookies();

function MobileText() {
  return (
    <Stack display={['flex', 'none']} zIndex={2}>
      <BrandHeading2
        fontSize={['8vw', '5xl']}
        as="h1"
        changeOffset={0}
        changeStyle={{
          color: 'brand.400',
        }}
        mb={-2}
      >
        {/* Get reservations at sold-out campgrounds or permits */}
        Get Instant Alerts When
      </BrandHeading2>
      <BrandHeading2
        fontSize={['8vw', '5xl']}
        as="h1"
        changeOffset={3}
        changeStyle={{
          color: 'brand.400',
        }}
      >
        {/* Get reservations at sold-out campgrounds or permits */}
        Spots Open Up
      </BrandHeading2>
      <Text fontSize={['1.1em', '2xl']}>
        We track the cancellations for you and alert you instantly by text or
        call. Be the first to book before anyone else.
      </Text>
    </Stack>
  );
}
function DesktopText() {
  return (
    <Stack display={['none', 'flex']} zIndex={2}>
      <BrandHeading2
        fontSize={['3xl', '5xl']}
        as="h1"
        changeOffset={3}
        changeStyle={{
          color: 'brand.400',
        }}
        mb={[7, 1, 1, 1, 1, 7]}
      >
        {/* Get reservations at sold-out campgrounds or permits */}
        {/* Secure your spot at fully booked campsites or permit. */}
        Get Instant Alerts When Spots Open Up
      </BrandHeading2>
      <Text fontSize={['lg', '2xl']} fontWeight={400} color="black">
        We track the cancellations for you and alert you instantly by text or
        call. Be the first to book before anyone else.
      </Text>
    </Stack>
  );
}

export default function Landing() {
  const {
    getPlans,
    setCurrentPage,
    setToast,
    setPlanSlug,
    setCode,
    code,
    banner,
    sendFBEvent,
  } = useUIStore(
    (state) => ({
      setCurrentPage: state.setCurrentPage,
      getPlans: state.getPlans,
      setToast: state.setToast,
      setPlanSlug: state.setPlanSlug,
      setCode: state.setCode,
      code: state.code,
      banner: state.banner,
      sendFBEvent: state.sendFBEvent,
    }),
    shallow
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [isImageLoading, setImageLoading] = useState(true);
  const navigate = useNavigate();

  const meta = {
    title: 'Reserve Nature - Track Campgrounds, Permits availability',
    summary:
      'Reserve Nature helps people secure campground, permits reservations by tracking availability once those are sold out',
    hero: 'https://reservenature.com/public/reserve-dark.png',
    url: 'https://reservenature.com',
    type: 'website',
  };

  // Track landing with facebook
  useEffect(() => {
    sendFBEvent({
      eventName: 'ViewContent',
      params: {
        content_name: 'Campground and permit availability alerts',
        content_category: 'home',
        content_type: 'product',
      },
    });
  }, []);

  useEffect(() => {
    // Set the affiliate cookie
    const affiliateCode = searchParams.get('aff');
    if (affiliateCode) {
      const sevenDaysAhead = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      cookies.set('affiliate', affiliateCode, {
        path: '/',
        expires: sevenDaysAhead,
      });
      searchParams.delete('aff');
      setSearchParams(searchParams, { replace: true });
    }
    setCurrentPage({ page: 'home' });
    getPlans();

    const paramCode = searchParams.get('code');
    if (paramCode) {
      setCode(paramCode);
      // searchParams.delete('code');
      setPlanSlug('nature-lovers-yearly-subscription');
    } else {
      setPlanSlug('');
    }

    const sessionParam = searchParams.get('session');
    if (sessionParam === 'expired') {
      setToast({
        title: 'Session expired',
        description: 'Please login again',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      searchParams.delete('session');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);
  let message = '';
  if (code === 'YEAROFF') {
    message = DISCOUNT_CODE[code].bannerText;
  } else {
    message = banner?.message;
  }

  const mobileHeight = 'calc(100vh - 18vh)';
  const desktopHeight = 'calc(100vh - 200px)';
  return (
    <Flex align={'stretch'} position="relative" direction={'column'}>
      <MetaInfo meta={meta} />
      {message && (
        <Link
          as={ReactLink}
          to={'/checkout'}
          position="absolute"
          w="100%"
          zIndex={1}
          top={0}
        >
          <ScaleFade
            initialScale={0.5}
            in={!!message}
            transition={{ enter: { duration: 0.4 } }}
          >
            <Flex
              w="100%"
              h={[8, 10]}
              justifyContent={'center'}
              alignItems={'center'}
              bg="secondary.500"
              color={'white'}
              fontWeight={600}
            >
              {message}
            </Flex>
          </ScaleFade>
        </Link>
      )}
      <Flex
        align="stretch"
        direction="column"
        minH={[mobileHeight, desktopHeight]}
      >
        <Box
          as="picture"
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h={[mobileHeight, desktopHeight]}
          zIndex={0}
        >
          <source srcSet="/hero-7-5-comp.avif" type="image/avif" />
          <source srcSet="/hero-7-5-comp.webp" type="image/webp" />
          <Box
            as="img"
            src="/hero-7-5-comp.jpg"
            alt="Mountain campground at sunrise with tents overlooking scenic valley"
            w="100%"
            h="100%"
            objectFit="cover"
            filter={isImageLoading ? 'blur(6px)' : 'blur(0px)'}
            transition="filter 0.3s"
            onLoad={() => setImageLoading(false)}
          />
        </Box>
        <Flex
          align="center"
          direction="column"
          minH="80vh"
          pt={[2, 6]}
          pb={[8, 12]}
          gap={[6, 0]}
        >
          <Stack
            w={['90%', '90%', '90%', '65%', '60%', '50%']}
            // my={[4, '4vh']}
            my={[4, 12, 12, 12, 12, 16]}
            textAlign="center"
            color={'black'}
            maxH="30vh"
          >
            <DesktopText />
            <MobileText />
          </Stack>
          <TrackWidget />
        </Flex>
        <Flex align="stretch" justify="center" direction="row"></Flex>
      </Flex>
      <StaticNotifications />
      <Testimonial />
    </Flex>
  );
}
